import { useCallback, useEffect, useState } from 'react';
import {
  useParams,
  generatePath,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import {
  ButtonPrimary,
  ButtonPseudo,
  PermissionDenied,
  PermissionEnum,
  Popup,
  ResponseError,
  responseErrorsResolver,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { useAppSelector } from '@store';
import AddRecordForm from '../../components/blacklist/add-record';
import {
  AddRecordFormValues,
  BlacklistData,
  EntityAddressesFormValues,
  EntityAliasesFormValues,
  RecordItem,
  EntityIdentificationsFormValues,
  EntityPositionsFormValues,
} from '../types';
import { useAddBlacklistItemMutation } from './store/services';

import ROUTES from '../../routes';

import cn from './blacklist.module.css';

const AddRecord = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [addresses, setAddresses] = useState<EntityAddressesFormValues[]>([]);
  const [aliases, setAliases] = useState<EntityAliasesFormValues[]>([]);
  const [identifications, setIdentifications] = useState<
    EntityIdentificationsFormValues[]
  >([]);
  const [positions, setPositions] = useState<EntityPositionsFormValues[]>([]);
  const [otherRecordErrors, setOtherRecordErrors] = useState<string[]>([]);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
    reset,
    setError,
  } = useForm<AddRecordFormValues>({
    defaultValues: {
      entityDOBs: [],
    },
  });

  const { activePermissions } = useAppSelector(
    (state) => state.amlDictionaries,
  );

  const { getBlacklistAction } = useOutletContext<{
    getBlacklistAction: (blacklistId: string) => void;
  }>();

  const [addBlacklistItem, { isLoading: requestInProgress, isSuccess }] =
    useAddBlacklistItemMutation();

  const dobs = watch('entityDOBs');

  const formIsDirty = Boolean(
    isDirty ||
    dobs?.length ||
    addresses?.length ||
    aliases?.length ||
    identifications?.length ||
    positions?.length,
  );

  const { itemId, recordId } = useParams();
  const { search, state: locationState } = useLocation();

  const navigate = useNavigate();

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');
    setOtherRecordErrors([]);

    const body: RecordItem = {};

    if (values?.entityTypeDesc) {
      body.entityTypeDesc = values.entityTypeDesc;
    }

    if (values?.firstName) {
      body.firstName = values.firstName;
    }

    if (values?.lastName) {
      body.lastName = values.lastName;
    }

    if (values?.middleName) {
      body.middleName = values.middleName;
    }

    if (values?.firstName || values?.lastName || values?.middleName) {
      body.name = `${values?.firstName || ''} ${values?.lastName || ''} ${
        values.middleName || ''
      }`;
    }

    if (values?.prefix) {
      body.prefix = values.prefix;
    }

    if (values?.suffix) {
      body.suffix = values.suffix;
    }

    if (values?.title) {
      body.title = values.title;
    }

    if (values?.gender) {
      body.gender = values.gender;
    }

    if (
      values?.entityDOBs &&
      values?.entityDOBs?.length &&
      values?.entityDOBs[0]
    ) {
      const dobsValues = values?.entityDOBs
        ?.filter((item) => item)
        ?.map((item) => format(new Date(item), 'yyyy-MM-dd'));

      const dobsList: {
        birthDate: {
          birthYear: string;
          birthMonth: string;
          birthDay: string;
        };
      }[] = [];

      dobsValues.forEach((item) => {
        const [year, month, day] = item.split('-');

        dobsList.push({
          birthDate: {
            birthYear: year,
            birthMonth:
              month.charAt(0) === '0' ? month.replace('0', '') : month,
            birthDay: day.charAt(0) === '0' ? day.replace('0', '') : day,
          },
        });
      });

      body.entityDOBs = dobsList;
    }

    if (addresses?.length) {
      const nextAddresses = addresses?.map((item) => {
        const result = { ...item };

        const emptyValues = Object.keys(item).filter(
          (key) => !item[key as keyof EntityAddressesFormValues],
        );

        emptyValues.push('id');

        if (emptyValues?.length) {
          emptyValues.forEach(
            (key) => delete result[key as keyof EntityAddressesFormValues],
          );
        }

        return result;
      });

      body.entityAddresses = nextAddresses?.filter(
        (item) => Object.keys(item)?.length,
      );
    }

    if (positions?.length) {
      const nextPositions = positions?.map((item) => {
        const result = { ...item };

        const emptyValues = Object.keys(item).filter(
          (key) => !item[key as keyof EntityPositionsFormValues],
        );

        emptyValues.push('id');

        if (emptyValues?.length) {
          emptyValues.forEach(
            (key) => delete result[key as keyof EntityPositionsFormValues],
          );
        }

        return result;
      });

      body.entityPositions = nextPositions?.filter(
        (item) => Object.keys(item)?.length,
      );
    }

    if (aliases?.length) {
      const nextAliases = aliases?.map((item) => {
        const result = { ...item };

        const emptyValues = Object.keys(item).filter(
          (key) => !item[key as keyof EntityAliasesFormValues],
        );

        emptyValues.push('id');

        if (emptyValues?.length) {
          emptyValues.forEach(
            (key) => delete result[key as keyof EntityAliasesFormValues],
          );
        }

        return result;
      });

      body.entityAliases = nextAliases?.filter(
        (item) => Object.keys(item)?.length,
      );
    }

    if (identifications?.length) {
      const nextIdentifications = identifications?.map((item) => {
        const result = { ...item };

        if (item?.issueDateValue) {
          const [issueYear, issueMonth, issueDay] = format(
            new Date(item?.issueDateValue),
            'yyyy-MM-dd',
          ).split('-');

          result.issueDate = {
            issueYear,
            issueMonth:
              issueMonth.charAt(0) === '0'
                ? issueMonth.replace('0', '')
                : issueMonth,
            issueDay:
              issueDay.charAt(0) === '0' ? issueDay.replace('0', '') : issueDay,
          };
        }

        if (item?.expirationDateValue) {
          const [expirationYear, expirationMonth, expirationDay] = format(
            new Date(item?.expirationDateValue),
            'yyyy-MM-dd',
          ).split('-');

          result.expirationDate = {
            expirationYear,
            expirationMonth:
              expirationMonth.charAt(0) === '0'
                ? expirationMonth.replace('0', '')
                : expirationMonth,
            expirationDay:
              expirationDay.charAt(0) === '0'
                ? expirationDay.replace('0', '')
                : expirationDay,
          };
        }

        const emptyValues = Object.keys(item).filter(
          (key) => !item[key as keyof EntityIdentificationsFormValues],
        );

        emptyValues.push('id');
        emptyValues.push('issueDateValue');
        emptyValues.push('expirationDateValue');

        if (emptyValues?.length) {
          emptyValues.forEach(
            (key) =>
              delete result[key as keyof EntityIdentificationsFormValues],
          );
        }

        return result;
      });

      body.entityIdentifications = nextIdentifications?.filter(
        (item) => Object.keys(item)?.length,
      );
    }

    const response = (await addBlacklistItem({
      id: itemId as string,
      body,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
        setOtherErrors: setOtherRecordErrors,
      });
    }
  });

  const setRecordData = (values: BlacklistData) => {
    if (values) {
      const { source } = values;

      const dobsValues = source?.entityDOBs
        ?.filter(
          (item) =>
            item.birthDate.birthYear &&
            item.birthDate.birthDay &&
            item.birthDate.birthMonth,
        )
        ?.map((item) =>
          new Date(
            `${item.birthDate.birthYear}-${
              item.birthDate.birthMonth &&
              Number(item.birthDate.birthMonth) < 10
                ? '0'
                : ''
            }${item.birthDate.birthMonth}-${
              item.birthDate.birthDay && Number(item.birthDate.birthDay) < 10
                ? '0'
                : ''
            }${item.birthDate.birthDay}`,
          ).toISOString(),
        );

      if (source?.entityAddresses) {
        setAddresses(
          source.entityAddresses?.map((item) => ({
            id: item.id,
            addressTypeDesc:
              item.addressTypeDesc?.length < 2 ? '' : item.addressTypeDesc,
            address1: item.address1?.length < 2 ? '' : item.address1,
            address2: item.address2?.length < 2 ? '' : item.address2,
            city: item.city?.length < 2 ? '' : item.city,
            postalCode: item.postalCode?.length < 2 ? '' : item.postalCode,
            country: item.country?.length < 2 ? '' : item.country,
            isoStandard: item.isoStandard?.length < 2 ? '' : item.isoStandard,
          })),
        );
      }

      if (source?.entityPositions) {
        setPositions(
          source.entityPositions?.map((item) => ({
            id: item.id,
            position: item.position?.length < 2 ? '' : item.position,
          })),
        );
      }

      if (source?.entityAliases) {
        setAliases(
          source.entityAliases?.map((item) => ({
            id: item.id,
            aliasTypeDesc:
              item.aliasTypeDesc?.length < 2 ? '' : item.aliasTypeDesc,
            englishDescription:
              item.englishDescription?.length < 2
                ? ''
                : item.englishDescription,
            name: item.name?.length < 2 ? '' : item.name,
            firstName: item.firstName?.length < 2 ? '' : item.firstName,
            lastName: item.lastName?.length < 2 ? '' : item.lastName,
            middleName: item.middleName?.length < 2 ? '' : item.middleName,
          })),
        );
      }

      if (source?.entityIdentifications) {
        setIdentifications(
          source.entityIdentifications?.map((item) => ({
            id: item.id,
            administrativeUnitName:
              item.administrativeUnitName?.length < 2
                ? ''
                : item.administrativeUnitName,
            isoStandard: item.isoStandard?.length < 2 ? '' : item.isoStandard,
            identificationIssuer:
              item.identificationIssuer?.length < 2
                ? ''
                : item.identificationIssuer,
            identificationTypeDesc:
              item.identificationTypeDesc?.length < 2
                ? ''
                : item.identificationTypeDesc,
            identificationNumber:
              item.identificationNumber?.length < 2
                ? ''
                : item.identificationNumber,
            issueDateValue: item?.issueDate
              ? new Date(
                  `${item?.issueDate.issueYear}-${
                    item?.issueDate.issueMonth &&
                    Number(item?.issueDate.issueMonth) < 10
                      ? '0'
                      : ''
                  }${item?.issueDate.issueMonth}-${
                    item?.issueDate.issueDay &&
                    Number(item?.issueDate.issueDay) < 10
                      ? '0'
                      : ''
                  }${item?.issueDate.issueDay}`,
                ).toISOString()
              : '',
            expirationDateValue: item?.expirationDate
              ? new Date(
                  `${item?.expirationDate.expirationYear}-${
                    item?.expirationDate.expirationMonth &&
                    Number(item?.expirationDate.expirationMonth) < 10
                      ? '0'
                      : ''
                  }${item?.expirationDate.expirationMonth}-${
                    item?.expirationDate.expirationDay &&
                    Number(item?.expirationDate.expirationDay) < 10
                      ? '0'
                      : ''
                  }${item?.expirationDate.expirationDay}`,
                ).toISOString()
              : '',
          })),
        );
      }

      reset({
        entityDOBs: dobsValues,
        entityTypeDesc: source?.entityTypeDesc
          ? source?.entityTypeDesc.toUpperCase()
          : '',
        gender: source?.gender || '',
        name: source?.name || '',
        firstName: source?.firstName || '',
        lastName: source?.lastName || '',
        middleName: source?.middleName || '',
        prefix: source?.prefix || '',
        suffix: source?.suffix || '',
        title: source?.title || '',
      });
    }
  };

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setGlobalError('');
    setOtherRecordErrors([]);

    setTimeout(() => {
      navigate(
        `${generatePath(ROUTES.BLACKLIST_ITEM, { itemId: itemId as string })}${
          search || ''
        }`,
      );
    }, 100);
  }, []);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (locationState && locationState.record) {
      setRecordData(locationState.record);
    }
  }, [locationState]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
      getBlacklistAction(itemId as string);
    }
  }, [isSuccess]);

  let content;

  if (
    !activePermissions?.includes(PermissionEnum.BLACKLISTS_ITEMS_CREATE_ACTION)
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <AddRecordForm
        control={control}
        errors={errors}
        setValue={setValue}
        dobs={dobs}
        addresses={addresses}
        setAddresses={setAddresses}
        aliases={aliases}
        setAliases={setAliases}
        identifications={identifications}
        setIdentifications={setIdentifications}
        otherRecordErrors={otherRecordErrors}
        positions={positions}
        setPositions={setPositions}
      />
    );
  }

  let buttonText = recordId ? 'Save changes' : 'Add record';

  if (requestInProgress) {
    buttonText = 'Loading...';
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={() => {
          if (formIsDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
            reset({});
          }
        }}
        errorSidepanel={globalError}
        title="Add record"
        footer={
          <div className={cn.addRecordControls}>
            <ButtonPrimary
              text={buttonText}
              onClick={onSubmit}
              disabled={requestInProgress}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() => {
                if (formIsDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                  reset({});
                }
              }}
              bold
            />
            {otherRecordErrors?.length > 0 && (
              <div className={cn.otherErrors}>There are some errors</div>
            )}
          </div>
        }
        sidepanelExpandedKey="blacklist-add-record"
        withoutExpand
        expanded
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={() => {
          setDirtyPopupVisible(false);
          handleClose();
        }}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default AddRecord;
